import type {CommentBoxConfig, CommentBoxHandle, Subject} from '@github-ui/comment-box'
import {CommentBox} from '@github-ui/comment-box'
import type {ValidationResult} from '@github-ui/entity-validators'
import {validateComment} from '@github-ui/entity-validators'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {testIdProps} from '@github-ui/test-id-props'
import {useToastContext} from '@github-ui/toast'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {BUTTON_LABELS, ERRORS, LABELS, TEST_IDS} from '../../constants'
import type {IssueCommentEditor_repository$key} from './__generated__/IssueCommentEditor_repository.graphql'
import type {IssueCommentEditorBodyFragment$key} from './__generated__/IssueCommentEditorBodyFragment.graphql'
import type {updateIssueCommentBodyMutation_Comment$key} from './mutations/__generated__/updateIssueCommentBodyMutation_Comment.graphql'
import {
  commitUpdateIssueCommentBodyMutation,
  updateCommentFragment,
} from './mutations/update-issue-comment-body-mutation'

type Props = {
  initialValue: string
  comment: IssueCommentEditorBodyFragment$key
  repo: IssueCommentEditor_repository$key
  cancelEdit: () => void
  onSave: () => void
  onChange: (value: string) => void
  commentBoxConfig?: CommentBoxConfig
  setIsSubmitting: (isSubmitting: boolean) => void
  isSubmitting: boolean
}

export const IssueCommentEditorFragment = graphql`
  fragment IssueCommentEditor_repository on IssueComment {
    repository {
      slashCommandsEnabled
      nameWithOwner
      databaseId
    }
    ...updateIssueCommentBodyMutation_Comment
    ...deleteIssueCommentMutation_Comment
  }
`

export const IssueCommentEditorBodyFragment = graphql`
  fragment IssueCommentEditorBodyFragment on IssueComment {
    id
    body
    bodyVersion
  }
`

export function IssueCommentEditor({
  initialValue = '',
  comment,
  repo,
  cancelEdit,
  onSave,
  onChange,
  commentBoxConfig,
  isSubmitting,
  setIsSubmitting,
}: Props) {
  const ref = useRef<CommentBoxHandle>(null)
  const {id, body, bodyVersion} = useFragment(IssueCommentEditorBodyFragment, comment)

  const commentRepository = useFragment<IssueCommentEditor_repository$key>(IssueCommentEditorFragment, repo)
  const commentData = useFragment<updateIssueCommentBodyMutation_Comment$key>(updateCommentFragment, commentRepository)
  const {repository} = commentRepository

  const repositoryDatabaseId = repository.databaseId!
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [currentBody, setCurrentBody] = useState<string>(initialValue || body)
  const [currentBodyVersion] = useState<string | undefined>(bodyVersion || undefined)

  const [validationResult, setValidationResult] = useState<ValidationResult>(validateComment(currentBody))
  useEffect(() => {
    setValidationResult(validateComment(currentBody))
  }, [currentBody])

  const onMarkdownChange = (newMarkdown: string) => {
    setCurrentBody(newMarkdown)
    onChange(newMarkdown)
  }

  const save = useCallback(
    (newId: string) => {
      setIsSubmitting(true)
      commitUpdateIssueCommentBodyMutation({
        environment,
        input: {id: newId, body: currentBody, bodyVersion: currentBodyVersion},
        onError: (error: Error) => {
          setIsSubmitting(false)
          addToast({
            type: 'error',
            message: error.message.startsWith(LABELS.staleCommentErrorPrefix)
              ? ERRORS.couldNotEditCommentStale
              : ERRORS.couldNotEditComment,
          })
          // this distinction is because for this specific error we don't want to close the editor (so the user can first copy the changes)
          if (!error.message.startsWith(LABELS.staleCommentErrorPrefix)) {
            onSave()
          } else {
            setValidationResult({
              isValid: false,
              errorMessage: ERRORS.couldNotEditCommentStale,
            })
          }
        },
        onCompleted: () => {
          setIsSubmitting(false)
          onSave()
        },
        commentData,
      })
    },
    [setIsSubmitting, environment, currentBody, currentBodyVersion, commentData, addToast, onSave],
  )

  const onCancel = () => {
    cancelEdit()
  }

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.focus()
    }
  }, [ref])

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue_comment',
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: repository.nameWithOwner,
        slashCommandsEnabled: repository.slashCommandsEnabled,
      },
    }
  }, [repository.nameWithOwner, repository.slashCommandsEnabled, repositoryDatabaseId])

  return (
    <CommentBox
      disabled={isSubmitting}
      subject={subject}
      ref={ref}
      value={(initialValue || currentBody) as SafeHTMLString}
      placeholder=""
      onSave={() => save(id)}
      onCancel={onCancel}
      validationResult={validationResult}
      onChange={onMarkdownChange}
      saveButtonText={BUTTON_LABELS.updateComment}
      userSettings={commentBoxConfig}
      saveButtonTrailingIcon={!isSubmitting}
      buttonSize="medium"
      sx={{border: 'none', py: 0}}
      {...testIdProps(TEST_IDS.commentBox('comment-editor'))}
    />
  )
}

try{ IssueCommentEditor.displayName ||= 'IssueCommentEditor' } catch {}